import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Play, Music, Disc3, Mic2, Mail } from "lucide-react";
import { fetchArtist, fetchGuestbookStats } from "../lib/api";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

const VINTAGE_STAGE =
    "https://static.prod-images.emergentagent.com/jobs/1a43c3aa-12b5-4d33-853d-116bd2011d29/images/65bb60e1e1d95afd3c2c3f6050bc740d8a0e00449d5cd1ee61fe146954130bcd.png";

const Section = ({ id, eyebrow, title, children, accent = "var(--rb-primary)" }) => (
    <section id={id} data-testid={`section-${id}`} className="relative px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto">
            {eyebrow && (
                <div
                    className="text-xs md:text-sm uppercase tracking-[0.32em] mb-4"
                    style={{ color: accent }}
                >
                    {eyebrow}
                </div>
            )}
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight">
                {title}
            </h2>
            <div className="mt-10">{children}</div>
        </div>
    </section>
);

export default function ArtistPage() {
    const { slug } = useParams();
    const [artist, setArtist] = useState(null);
    const [error, setError] = useState(null);
    const [gbCount, setGbCount] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchArtist(slug)
            .then((d) => !cancelled && setArtist(d))
            .catch(() => !cancelled && setError("Inhalt konnte nicht geladen werden."));
        fetchGuestbookStats()
            .then((s) => !cancelled && setGbCount(s[slug] ?? 0))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (slug !== "thomas" && slug !== "bernhard") return <Navigate to="/" replace />;

    const isThomas = slug === "thomas";
    const accent = isThomas ? "var(--rb-primary)" : "var(--rb-secondary)";
    const otherSlug = isThomas ? "bernhard" : "thomas";
    const otherName = isThomas ? "Bernhard Rabitsch" : "Thomas Rabitsch";

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center text-zinc-400">
                {error}
            </div>
        );
    }

    if (!artist) {
        return (
            <div className="min-h-screen flex items-center justify-center text-zinc-500 uppercase tracking-[0.4em] text-xs">
                Lade …
            </div>
        );
    }

    return (
        <div className="App relative min-h-screen bg-[var(--rb-bg)]" data-testid={`artist-page-${slug}`}>
            <Navbar />

            {/* HERO */}
            <header className="relative w-full h-[90vh] min-h-[640px] overflow-hidden">
                <motion.div
                    initial={{ scale: 1.12 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 bg-cover bg-no-repeat"
                    style={{
                        backgroundImage: `url(${artist.hero_image})`,
                        backgroundPosition: "center top",
                        filter: "brightness(0.7) contrast(1.02)",
                        WebkitMaskImage:
                            "linear-gradient(to bottom, black 55%, rgba(0,0,0,0.4) 80%, transparent 100%)",
                        maskImage:
                            "linear-gradient(to bottom, black 55%, rgba(0,0,0,0.4) 80%, transparent 100%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black" />
                <div className="absolute inset-0 grain" />

                <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.4 }}
                    >
                        <Link
                            to="/"
                            data-testid="back-home"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-zinc-400 hover:text-white transition-colors mb-6"
                        >
                            <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
                        </Link>
                        <div
                            className="text-xs md:text-sm uppercase tracking-[0.32em] mb-3"
                            style={{ color: accent }}
                        >
                            {artist.subtitle}
                        </div>
                        <h1 className="font-display text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight text-white leading-[0.9]">
                            {artist.name}
                        </h1>
                    </motion.div>
                </div>
            </header>

            {/* BIO */}
            <Section id="bio" eyebrow="Kurzbiografie" title="Über den Künstler" accent={accent}>
                <p className="text-lg md:text-xl text-zinc-300 leading-relaxed max-w-4xl">
                    {artist.biography}
                </p>
            </Section>

            {/* TIMELINE */}
            <Section id="timeline" eyebrow="Karriere" title="Timeline" accent={accent}>
                <div className="relative pl-6 md:pl-10">
                    <div className="absolute left-1 md:left-2 top-2 bottom-2 w-px timeline-line" />
                    <div className="space-y-14">
                        {(artist.timeline || []).map((block, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.05 }}
                                data-testid={`timeline-${i}`}
                                className="relative"
                            >
                                <div
                                    className="absolute -left-[26px] md:-left-[34px] top-2 w-3 h-3 rounded-full"
                                    style={{ background: accent, boxShadow: `0 0 24px ${accent}` }}
                                />
                                <div className="font-display text-3xl md:text-4xl text-white mb-4">
                                    {block.decade}
                                </div>
                                <ul className="space-y-3 max-w-3xl">
                                    {block.events.map((ev, j) => (
                                        <li key={j} className="flex gap-3 text-zinc-300 leading-relaxed">
                                            <span
                                                className="mt-2 block w-1.5 h-1.5 rounded-full shrink-0"
                                                style={{ background: accent }}
                                            />
                                            <span>{ev}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* BANDS */}
            <Section id="bands" eyebrow="" title="Bands & Projekte" accent={accent}>
                <ul className="space-y-3 max-w-3xl">
                    {(artist.bands || []).map((b, i) => (
                        <motion.li
                            key={b + i}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            data-testid={`band-${i}`}
                            className="flex items-center gap-3 text-zinc-300 leading-relaxed"
                        >
                            <span
                                className="block w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ background: accent }}
                            />
                            <span>{b}</span>
                        </motion.li>
                    ))}
                </ul>
            </Section>

            {/* GALLERY */}
            <Section id="gallery" eyebrow="Visuell" title="Bildergalerie" accent={accent}>
                {(artist.gallery || []).length === 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="placeholder-tile aspect-[4/5] flex items-center justify-center"
                                data-testid={`gallery-placeholder-${i}`}
                            >
                                <div className="text-zinc-600 text-xs uppercase tracking-[0.3em]">
                                    Bild folgt
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {artist.gallery.map((src, i) => (
                        <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`gallery-image-${i}`}
                            className="block aspect-[4/5] overflow-hidden bg-zinc-900 group"
                        >
                            <img
                                src={src}
                                alt={`${artist.name} ${i + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                        </a>
                    ))}
                </div>
                )}
                <div className="mt-6 text-zinc-500 text-sm">
                </div>
            </Section>

            {/* FAN SECTION */}
            <section
                data-testid="section-fan"
                className="relative px-6 md:px-10 py-20 md:py-28 overflow-hidden"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${VINTAGE_STAGE})`, filter: "brightness(0.35) saturate(1.2)" }}
                />
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                <div className="relative z-10 max-w-[1400px] mx-auto">
                    <div
                        className="text-xs md:text-sm uppercase tracking-[0.32em] mb-4"
                        style={{ color: accent }}
                    >
                        Persönliche Auswahl
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white tracking-tight">
                        {artist.fan_section?.title || "Fan Section"}
                    </h2>
                    {artist.fan_section?.intro && (
                        <p className="mt-4 max-w-2xl text-zinc-300 text-lg">{artist.fan_section.intro}</p>
                    )}

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                        <FanCard
                            icon={<Mic2 className="w-5 h-5" />}
                            label={artist.fan_section?.interview_label || "Interview"}
                            title="Interview"
                            url={artist.fan_section?.interview_url}
                            embed={artist.fan_section?.interview_embed}
                            kind={slug === "bernhard" ? "youtube" : "spotify-episode"}
                            accent={accent}
                            testid="fan-interview"
                        />
                        <FanCard
                            icon={<Music className="w-5 h-5" />}
                            label="Lieblingssong"
                            title={artist.fan_section?.favorite_song_title}
                            url={artist.fan_section?.favorite_song_url}
                            embed={artist.fan_section?.favorite_song_embed}
                            kind="spotify-track"
                            accent={accent}
                            testid="fan-song"
                        />
                        <FanCard
                            icon={<Disc3 className="w-5 h-5" />}
                            label="Lieblingsalbum"
                            title={artist.fan_section?.favorite_album_title}
                            url={artist.fan_section?.favorite_album_url}
                            embed={artist.fan_section?.favorite_album_embed}
                            kind="spotify-album"
                            accent={accent}
                            testid="fan-album"
                        />
                    </div>
                </div>
            </section>

            {/* LINKS */}
            <Section id="links" eyebrow="Mehr erfahren" title="Wikipedia & Links" accent={accent}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(artist.links || []).map((l, i) => (
                        <a
                            key={i}
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`link-${i}`}
                            className="group glass p-6 flex items-center justify-between gap-4 border border-white/5 hover:border-[var(--rb-primary)] transition-all"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-[0.32em] text-zinc-500 mb-1">
                                    Externer Link
                                </div>
                                <div className="font-display text-xl md:text-2xl text-white group-hover:text-[var(--rb-secondary)] transition-colors">
                                    {l.label}
                                </div>
                                <div className="text-xs text-zinc-500 mt-1 truncate max-w-[460px]">{l.url}</div>
                            </div>
                            <ExternalLink className="w-5 h-5 text-zinc-400 group-hover:text-[var(--rb-primary)] transition-colors shrink-0" />
                        </a>
                    ))}
                </div>
            </Section>

            {/* GUESTBOOK CTA */}
            <section
                data-testid={`section-guestbook-cta-${slug}`}
                className="relative px-6 md:px-10 py-20 md:py-24 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto grid md:grid-cols-[auto_1fr_auto] gap-8 md:gap-10 items-center">
                    <div
                        className="w-16 h-16 md:w-20 md:h-20 grid place-items-center border border-white/10"
                        style={{ borderColor: `${accent}` }}
                    >
                        <Mail className="w-6 h-6 md:w-7 md:h-7" style={{ color: accent }} strokeWidth={1.4} />
                    </div>
                    <div>
                        <div
                            className="text-xs uppercase tracking-[0.32em] mb-2"
                            style={{ color: accent }}
                        >
                            Einträge zu {artist.name.split(" ")[0]}
                        </div>
                        <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
                            Fans haben bereits{" "}
                            <span style={{ color: accent }}>{gbCount ?? 0}</span>{" "}
                            {gbCount === 1 ? "Eintrag" : "Einträge"} zu{" "}
                            {artist.name.split(" ")[0]} hinterlassen.
                        </h3>
                    </div>
                    <Link
                        to={`/gaestebuch?category=${slug}`}
                        data-testid={`guestbook-cta-${slug}`}
                        className="group flex items-center gap-3 border border-white/10 hover:border-[var(--rb-primary)] px-5 py-3 md:px-6 md:py-4 uppercase tracking-[0.28em] text-xs md:text-sm text-white transition-all hover:-translate-y-0.5"
                    >
                        Alle Einträge ansehen
                        <ArrowLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </section>

            {/* OTHER ARTIST CTA */}
            <section className="px-6 md:px-10 py-16">
                <div className="max-w-[1400px] mx-auto border-t border-white/5 pt-12 flex flex-col md:flex-row gap-4 md:items-center justify-between">
                    <div className="text-zinc-400 uppercase tracking-[0.32em] text-xs">Weiter zu</div>
                    <Link
                        to={`/${otherSlug}`}
                        data-testid="cta-other-artist"
                        className="group flex items-center gap-4"
                    >
                        <span className="font-display text-3xl md:text-5xl text-white group-hover:text-[var(--rb-primary)] transition-colors">
                            {otherName}
                        </span>
                        <Play className="w-6 h-6 text-zinc-500 group-hover:text-[var(--rb-primary)] transition-colors" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const FanCard = ({ icon, label, title, url, embed, kind, accent, testid }) => {
    return (
        <div data-testid={testid} className="glass p-5 md:p-6 flex flex-col">
            <div
                className="flex items-center gap-2 text-xs uppercase tracking-[0.32em] mb-3"
                style={{ color: accent }}
            >
                {icon}
                <span>{label}</span>
            </div>
            {title && (
                <div className="font-display text-lg md:text-xl text-white mb-4">{title}</div>
            )}
            {embed && (
                <div className="relative w-full overflow-hidden bg-black/40" data-testid={`${testid}-embed`}>
                    {kind === "youtube" ? (
                        <div className="aspect-video">
                            <iframe
                                src={embed}
                                title={title || label}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    ) : (
                        <iframe
                            src={embed}
                            title={title || label}
                            className="w-full"
                            style={{ height: kind === "spotify-episode" ? 232 : kind === "spotify-album" ? 380 : 152 }}
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                        />
                    )}
                </div>
            )}
            {url && (
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-zinc-400 hover:text-white transition-colors"
                >
                    Auf Plattform öffnen <ExternalLink className="w-3.5 h-3.5" />
                </a>
            )}
        </div>
    );
};
